import React, { useCallback } from 'react';
import { styled } from '@mui/material/styles';
import { Typography } from '@mui/material';
import NodeConditionRow from './NodeConditionRow';
import NodeAddButton from './NodeAddButton';
import { NODE_UI, stopPropagation } from './nodeUiStyles';

/**
 * NodeConditionBuilder — Full condition builder with nested conditions,
 * AND/OR logic, if/else-if/else blocks.
 * Used by: Conditional Path, Conditional Logic, Conditional WF Paths.
 *
 * Renders a list of NodeConditionRows with add/remove/duplicate.
 * The caller provides renderKey and renderValue for custom inputs.
 */

const Root = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: NODE_UI.spacing.sm,
  width: '100%',
});

const Label = styled(Typography)(({ theme }) => ({
  fontSize: NODE_UI.fontSize.label,
  fontWeight: NODE_UI.fontWeight.label,
  color: theme.palette.text.secondary,
  lineHeight: 1.4,
  userSelect: 'none',
}));

const BlockContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: NODE_UI.spacing.sm,
  padding: NODE_UI.spacing.md,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: NODE_UI.borderRadius,
  backgroundColor: theme.palette.background.paper,
}));

const BlockLabel = styled(Typography)(({ theme }) => ({
  fontSize: NODE_UI.fontSize.value,
  fontWeight: 600,
  color: theme.palette.text.primary,
}));

const NodeConditionBuilder = React.memo(function NodeConditionBuilder({
  label,
  conditions = [],
  onConditionsChange,
  renderKey,
  renderValue,
  functionOptions,
  addLabel = 'Add Condition',
  createCondition,
  sx,
}) {
  const defaultCreate = () => ({
    logic: 'AND',
    key: '',
    function: 'EQUAL',
    value: '',
  });

  const handleAdd = useCallback(() => {
    const newCondition = createCondition ? createCondition() : defaultCreate();
    onConditionsChange?.([...conditions, newCondition]);
  }, [conditions, onConditionsChange, createCondition]);

  const handleUpdate = useCallback((index, field, value) => {
    const next = [...conditions];
    next[index] = { ...next[index], [field]: value };
    onConditionsChange?.(next);
  }, [conditions, onConditionsChange]);

  const handleDelete = useCallback((index) => {
    onConditionsChange?.(conditions.filter((_, i) => i !== index));
  }, [conditions, onConditionsChange]);

  const handleDuplicate = useCallback((index) => {
    const next = [...conditions];
    next.splice(index + 1, 0, { ...conditions[index] });
    onConditionsChange?.(next);
  }, [conditions, onConditionsChange]);

  return (
    <Root onMouseDown={stopPropagation} onClick={stopPropagation} onPointerDown={stopPropagation} sx={sx}>
      {label && <Label>{label}</Label>}
      {conditions.map((condition, index) => (
        <NodeConditionRow
          key={condition._id || index}
          logic={condition.logic}
          onLogicChange={(val) => handleUpdate(index, 'logic', val)}
          keyValue={condition.key}
          onKeyChange={(val) => handleUpdate(index, 'key', val)}
          functionValue={condition.function}
          onFunctionChange={(val) => handleUpdate(index, 'function', val)}
          value={condition.value}
          onValueChange={(val) => handleUpdate(index, 'value', val)}
          renderKey={renderKey}
          renderValue={renderValue}
          functionOptions={functionOptions}
          onDelete={conditions.length > 1 ? () => handleDelete(index) : undefined}
          onDuplicate={() => handleDuplicate(index)}
          isFirst={index === 0}
        />
      ))}
      <NodeAddButton label={addLabel} onClick={handleAdd} />
    </Root>
  );
});

export default NodeConditionBuilder;
